import { type CreditMemoItem, type InvoiceItem, type InvoiceSuggestion, type InvoiceStatus, type JobEstimateItem, MONEY_SOURCE_LABELS, PAYMENT_METHOD_LABELS, formatPercentMilli } from "@nmt/shared";
import { AfterSend, SendToCustomer } from "../components/email-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, patch, post } from "../api";
import { Alert, Badge, Button, Checkbox, Dialog, Empty, Field, Loading, Panel, PanelHeader, SelectField, TextAreaField, cx, formatCents, formatDate } from "../components/ui";
import { useSession } from "../session";
import { RichText } from "../components/rich-text";
import { ProgressBillingPanels } from "./ProgressBilling";

const STATUS: Record<InvoiceStatus, { label: string; tone: "ok" | "wait" | "off" | "brand" }> = {
  draft: { label: "Draft", tone: "wait" },
  sent: { label: "Sent", tone: "brand" },
  partial: { label: "Partly paid", tone: "brand" },
  paid: { label: "Paid", tone: "ok" },
  void: { label: "Void", tone: "off" },
};

/** The job's Invoices & Payments tab. */
export function JobInvoices({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const list = useQuery({ queryKey: ["invoices", jobId], queryFn: () => get<InvoiceItem[]>(`/api/jobs/${jobId}/invoices`) });
  const suggestions = useQuery({ queryKey: ["invoice-suggestions", jobId], queryFn: () => get<InvoiceSuggestion[]>(`/api/jobs/${jobId}/invoice-suggestions`) });
  const [editing, setEditing] = useState<EditorStart | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const memos = useQuery({ queryKey: ["credit-memos", jobId], queryFn: () => get<CreditMemoItem[]>(`/api/jobs/${jobId}/credit-memos`) });
  const estimates = useQuery({ queryKey: ["estimates", jobId], queryFn: () => get<JobEstimateItem[]>(`/api/jobs/${jobId}/estimates`) });
  const live = list.data?.filter((i) => i.status !== "void") ?? [];
  const billed = live.reduce((s, i) => s + i.totalCents, 0);
  const paid = live.reduce((s, i) => s + i.paidCents, 0);
  // Credit memos and write-offs applied, plus credit not yet applied to an invoice.
  const credited = live.reduce((s, i) => s + i.creditedCents, 0) + (memos.data ?? []).reduce((s, m) => s + m.unappliedCents, 0);
  const edit = can("invoices.edit");
  return (
    <div className="flex flex-col gap-5">
      <ProgressBillingPanels jobId={jobId} onOpenInvoice={setOpen} />
      <Panel>
        <PanelHeader
          title="Invoices and payments"
          description={
            live.length
              ? [`${formatCents(billed)} invoiced`, `${formatCents(paid)} paid`, credited ? `${formatCents(credited)} credits and write-offs` : null, `${formatCents(Math.max(0, billed - paid - credited))} due`].filter(Boolean).join(" · ")
              : "Invoice each payment when it's time (deductible, ACV, depreciation, a draw)."
          }
          action={
            edit && (
              <Button variant="primary" className="px-3 py-1.5" onClick={() => setEditing({ title: "", lines: [{ description: "", amount: "" }] })}>
                New invoice
              </Button>
            )
          }
        />
        {suggestions.data?.some((s) => s.part) && (
          <div className="border-b border-mist">
            <div className="px-5 pt-3 text-sm font-semibold text-ink-soft">Payments from the signed proposal</div>
            <ul className="px-5 py-2 text-sm">
              {suggestions.data
                .filter((s) => s.part)
                .map((s) => (
                  <li key={`${s.proposalId}${s.part}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-2 last:border-0">
                    <span className="font-semibold">{s.label}</span>
                    <span className="min-w-0 flex-1 truncate text-muted">{[s.percentMilli != null ? formatPercentMilli(s.percentMilli) : null, s.when, s.proposalTitle].filter(Boolean).join(" · ")}</span>
                    <span>{formatCents(s.amountCents)}</span>
                    {s.invoice ? (
                      <button className="font-semibold text-brand hover:underline" onClick={() => setOpen(s.invoice!.id)}>
                        Invoice {s.invoice.number} · {STATUS[s.invoice.status].label}
                      </button>
                    ) : (
                      <>
                        <Badge tone="wait">Upcoming</Badge>
                        {edit && (
                          <Button
                            variant="secondary"
                            className="px-3 py-1"
                            onClick={() =>
                              setEditing({
                                title: `${s.label} - ${s.proposalTitle}`,
                                lines: [{ description: `${s.proposalTitle}: ${s.label}${s.percentMilli != null ? ` (${formatPercentMilli(s.percentMilli)} of the contract)` : ""}`, amount: (s.amountCents / 100).toFixed(2) }],
                                link: { proposalId: s.proposalId!, part: s.part! },
                              })
                            }
                          >
                            Invoice this
                          </Button>
                        )}
                      </>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        )}
        {edit && suggestions.data?.some((s) => !s.invoiced && !s.part) && (
          <div className="flex flex-wrap items-center gap-2 border-b border-mist px-5 py-3 text-sm">
            <span className="text-muted">From the signed proposal:</span>
            {suggestions.data
              .filter((s) => !s.invoiced && !s.part)
              .map((s) => (
                <button key={s.label} title={s.when} className="rounded-full bg-mist px-3 py-1 font-semibold hover:bg-line" onClick={() => setEditing({ title: s.label, lines: [{ description: s.label, amount: (s.amountCents / 100).toFixed(2) }] })}>
                  {s.label} {formatCents(s.amountCents)}
                </button>
              ))}
          </div>
        )}
        {list.isLoading ? (
          <div className="px-5">
            <Loading />
          </div>
        ) : !list.data?.length ? (
          <Empty>No invoices yet.</Empty>
        ) : (
          <ul>
            {list.data.map((i) => (
              <li key={i.id} className="border-b border-mist last:border-0">
                <button onClick={() => setOpen(i.id)} className="flex w-full flex-wrap items-center gap-3 px-5 py-3 text-left hover:bg-mist/50">
                  <span className="font-semibold">{i.externalNumber ? `Invoice ${i.externalNumber}` : `Invoice ${i.number}`}</span>
                  <Badge tone={STATUS[i.status].tone}>{STATUS[i.status].label}</Badge>
                  {i.source !== "crm" && <Badge tone="off">From {MONEY_SOURCE_LABELS[i.source]}</Badge>}
                  <QbBadge invoice={i} />
                  {i.payments.some((p) => p.status === "pending") && <Badge tone="wait">Payment processing</Badge>}
                  <span className="min-w-0 flex-1 truncate text-sm text-muted">{[i.title, i.dueDate ? `due ${formatDate(i.dueDate)}` : null].filter(Boolean).join(" · ")}</span>
                  <span className="text-sm">{i.status === "paid" || i.status === "void" ? formatCents(i.totalCents) : `${formatCents(i.balanceCents)} due of ${formatCents(i.totalCents)}`}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {!!memos.data?.length && (
        <Panel>
          <PanelHeader title="Credit memos" description="From QuickBooks. What's applied shows on the invoices; the rest is still owed back to the customer." />
          <ul>
            {memos.data.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-3 border-b border-mist px-5 py-3 text-sm last:border-0">
                <span className="font-semibold">Credit memo {m.number || ""}</span>
                <span className="text-muted">{m.date ? formatDate(m.date) : ""}</span>
                <span className="min-w-0 flex-1 truncate text-muted">{m.lines.map((l) => l.description).join(", ")}</span>
                <span>
                  −{formatCents(m.totalCents)}
                  {m.unappliedCents > 0 && <span className="text-wait"> · {formatCents(m.unappliedCents)} not applied yet</span>}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {!!estimates.data?.length && (
        <Panel>
          <PanelHeader title="Estimates from JobNimbus" description="For reference. New estimates are proposals." />
          <ul>
            {estimates.data.map((e) => (
              <li key={e.id} className="border-b border-mist last:border-0">
                <details>
                  <summary className="flex cursor-pointer flex-wrap items-center gap-3 px-5 py-3 text-sm hover:bg-mist/50">
                    <span className="font-semibold">Estimate {e.number}</span>
                    {e.status && <Badge tone="off">{e.status}</Badge>}
                    <span className="min-w-0 flex-1 truncate text-muted">{[e.title, e.date ? formatDate(e.date) : null].filter(Boolean).join(" · ")}</span>
                    <span>{formatCents(e.totalCents)}</span>
                  </summary>
                  <table className="mx-5 mb-3 w-[calc(100%-2.5rem)] text-sm">
                    <tbody>
                      {e.lines.map((l, k) => (
                        <tr key={k} className="border-b border-mist">
                          <td className="py-1.5 whitespace-pre-wrap">{l.description}</td>
                          <td className="py-1.5 text-right align-top">{formatCents(l.amountCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {editing && <InvoiceEditor jobId={jobId} start={editing} onClose={() => setEditing(null)} onSaved={(i) => (setEditing(null), setOpen(i.id))} />}
      {open && <InvoiceDialog id={open} jobId={jobId} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Whether QuickBooks has this invoice, and whether it agrees. */
function QbBadge({ invoice: i }: { invoice: InvoiceItem }) {
  if (i.status === "void") return i.voidReason ? <Badge tone="off">{i.voidReason}</Badge> : null;
  if (i.mismatch) return <span title={i.mismatch}><Badge tone="wait">Differs from QuickBooks</Badge></span>;
  if (i.quickbooks) return <span title={`Matched ${i.quickbooks.matchedBy === "number" ? "by invoice number" : i.quickbooks.matchedBy === "amount" ? "by amount and date" : i.quickbooks.matchedBy === "created" ? "(made in QuickBooks)" : "(linked)"}`}><Badge tone="ok">In QuickBooks</Badge></span>;
  if (i.qbCheckedAt) return <Badge tone="wait">Not found in QuickBooks</Badge>;
  return null;
}

type EditorStart = { title: string; lines: { description: string; amount: string }[]; link?: { proposalId: string; part: string } };

function InvoiceEditor({ jobId, start, invoice, onClose, onSaved }: { jobId: string; start: EditorStart; invoice?: InvoiceItem; onClose: () => void; onSaved: (i: InvoiceItem) => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(start.title);
  const [lines, setLines] = useState(start.lines);
  const [due, setDue] = useState(invoice?.dueDate ?? "");
  const [memo, setMemo] = useState(invoice?.memo ?? "");
  const cents = lines.map((l) => (/^\d+(\.\d{1,2})?$/.test(l.amount.replace(/[$,\s]/g, "")) ? Math.round(Number(l.amount.replace(/[$,\s]/g, "")) * 100) : null));
  const ok = lines.every((l, i) => l.description.trim() && cents[i] !== null);
  const total = cents.reduce<number>((s, c) => s + (c ?? 0), 0);
  const save = useMutation({
    mutationFn: () => {
      const body = { title, lines: lines.map((l, i) => ({ description: l.description.trim(), amountCents: cents[i]! })), dueDate: due || null, memo };
      return invoice ? patch<InvoiceItem>(`/api/invoices/${invoice.id}`, body) : post<InvoiceItem>(`/api/jobs/${jobId}/invoices`, { ...body, ...(start.link ? { proposalId: start.link.proposalId, proposalPart: start.link.part } : {}) });
    },
    onSuccess: async (i) => {
      await qc.invalidateQueries({ queryKey: ["invoices", jobId] });
      await qc.invalidateQueries({ queryKey: ["invoice-suggestions", jobId] });
      onSaved(i);
    },
  });
  return (
    <Dialog
      wide
      title={invoice ? `Edit invoice ${invoice.number}` : "New invoice"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!ok || total <= 0} onClick={() => save.mutate()}>
            Save · {formatCents(total)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Insurance Deductible" />
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-[1fr_140px_auto] items-start gap-2">
            <TextAreaField label={i === 0 ? "Description" : ""} aria-label="Description" rows={2} maxLength={4000} value={l.description} onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, description: e.target.value } : x)))} />
            <Field label={i === 0 ? "Amount" : ""} aria-label="Amount" inputMode="decimal" value={l.amount} onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, amount: e.target.value } : x)))} error={l.amount && cents[i] === null ? " " : undefined} />
            <button className={cx("px-1 py-2 text-muted hover:text-danger", i === 0 && "mt-6")} aria-label="Remove line" disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, k) => k !== i))}>
              ✕
            </button>
          </div>
        ))}
        <div>
          <Button variant="quiet" className="px-3 py-1.5" onClick={() => setLines([...lines, { description: "", amount: "" }])}>
            + Line
          </Button>
        </div>
        <Field label="Due (optional)" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        <TextAreaField label="Note to the customer (optional)" value={memo} onChange={(e) => setMemo(e.target.value)} rows={2} />
      </div>
    </Dialog>
  );
}

function InvoiceDialog({ id, jobId, onClose }: { id: string; jobId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const q = useQuery({ queryKey: ["invoice", id], queryFn: () => get<InvoiceItem>(`/api/invoices/${id}`) });
  const [sending, setSending] = useState(false);
  const [replaced, setReplaced] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [editing, setEditing] = useState(false);
  const done = async (i: InvoiceItem) => {
    qc.setQueryData(["invoice", id], i);
    await qc.invalidateQueries({ queryKey: ["invoices", jobId] });
    await qc.invalidateQueries({ queryKey: ["invoice-suggestions", jobId] });
    await qc.invalidateQueries({ queryKey: ["progress", jobId] });
  };
  const send = async () => {
    const sent = await post<InvoiceItem>(`/api/invoices/${id}/send`);
    await done(sent);
    return sent.payUrl!;
  };
  const replace = useMutation({ mutationFn: () => post<InvoiceItem>(`/api/invoices/${id}/new-link`), onSuccess: async (x) => (await done(x), setReplaced(x.payUrl ?? null)) });
  const overLimit = useMutation({ mutationFn: (allow: boolean) => post<InvoiceItem>(`/api/invoices/${id}/card-over-limit`, { allow }), onSuccess: done });
  const voidIt = useMutation({ mutationFn: () => post<InvoiceItem>(`/api/invoices/${id}/void`), onSuccess: done });
  const removePay = useMutation({ mutationFn: (pid: string) => del<InvoiceItem>(`/api/invoices/${id}/payments/${pid}`), onSuccess: done });
  const i = q.data;
  const edit = can("invoices.edit");
  if (editing && i) return <InvoiceEditor jobId={jobId} invoice={i} start={{ title: i.title, lines: i.lines.map((l) => ({ description: l.description, amount: (l.amountCents / 100).toFixed(2) })) }} onClose={() => setEditing(false)} onSaved={async (x) => (await done(x), setEditing(false))} />;
  if (sending && i)
    return (
      <SendToCustomer
        kind="invoice"
        id={id}
        jobId={jobId}
        note={i.sentAt ? "It goes with the same pay link as before. Then you can check the email or text before it goes." : "This marks the invoice sent, makes the customer's pay link (the same one every time), and puts it in QuickBooks. Then you can check the email or text before it goes."}
        send={send}
        onClose={() => setSending(false)}
      />
    );
  if (replaced) return <AfterSend how="link" kind="invoice" id={id} link={replaced} jobId={jobId} onClose={() => setReplaced(null)} linkNote="The new pay link. The old one doesn't work anymore. Copy it now, or send it below." />;
  if (recording && i) return <RecordPayment invoice={i} onClose={() => setRecording(false)} onSaved={async (x) => (await done(x), setRecording(false))} />;
  return (
    <Dialog
      wide
      title={i ? (i.externalNumber ? `Invoice ${i.externalNumber}` : `Invoice ${i.number}`) : "Invoice"}
      onClose={onClose}
      footer={
        i &&
        edit &&
        (i.source === "crm" || i.quickbooks) &&
        i.status !== "void" && (
          <div className="flex w-full flex-wrap items-center gap-2">
            {i.source === "crm" && i.paidCents === 0 && !i.payments.some((p) => p.status === "pending") && (
              <Button variant="quiet" busy={voidIt.isPending} onClick={() => window.confirm("Void this invoice? Its pay link stops working.") && voidIt.mutate()}>
                Void
              </Button>
            )}
            {i.sentAt && i.status !== "paid" && (
              <Button variant="quiet" busy={replace.isPending} onClick={() => window.confirm("Make a new pay link? The old one stops working, so anyone with it (or an old PDF) can't pay with it. Only for a link that went to the wrong person.") && replace.mutate()}>
                Replace pay link
              </Button>
            )}
            <span className="flex-1" />
            {i.source === "crm" && i.status !== "paid" && <Button onClick={() => setEditing(true)}>Edit</Button>}
            {i.status !== "paid" && <Button onClick={() => setRecording(true)}>Record payment</Button>}
            {i.status !== "paid" && (
              <Button variant="primary" onClick={() => setSending(true)}>
                {i.sentAt ? "Send again…" : "Send to customer…"}
              </Button>
            )}
          </div>
        )
      }
    >
      {!i ? (
        <Loading />
      ) : (
        <div className="flex flex-col gap-4 text-sm">
          {(voidIt.error || removePay.error || replace.error) && <Alert>{errorMessage(voidIt.error ?? removePay.error ?? replace.error)}</Alert>}
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={STATUS[i.status].tone}>{STATUS[i.status].label}</Badge>
            <QbBadge invoice={i} />
            <a href={`/api/invoices/${id}/pdf`} target="_blank" rel="noreferrer" className="order-last ml-auto font-semibold text-brand hover:underline">
              Download PDF
            </a>
            <span className="text-muted">{[i.title, i.invoiceDate ? `dated ${formatDate(i.invoiceDate)}` : null, i.dueDate ? `due ${formatDate(i.dueDate)}` : null, i.sentAt && i.source === "crm" ? `sent ${formatDate(i.sentAt)}` : null].filter(Boolean).join(" · ")}</span>
          </div>
          {i.source !== "crm" && (
            <Alert tone="info">
              From {MONEY_SOURCE_LABELS[i.source]}
              {i.externalNumber ? ` (${i.externalNumber})` : ""}. Change or void it in QuickBooks.{" "}
              {i.quickbooks ? "You can send it and take payments here; they go to QuickBooks on this invoice." : "Link it to QuickBooks (Sync page) to send it or take payments here."}
            </Alert>
          )}
          {i.mismatch && <Alert>{i.mismatch} QuickBooks is the one to trust; fix whichever is wrong in QuickBooks.</Alert>}
          {edit && i.status !== "void" && i.status !== "paid" && (i.source === "crm" || i.quickbooks) && (
            <Checkbox
              label="Allow card payment over the card limit"
              description="This invoice only. The card surcharge still applies; bank (ACH) stays free."
              checked={i.cardOverLimit}
              disabled={overLimit.isPending}
              onChange={(v) => overLimit.mutate(v)}
            />
          )}
          {overLimit.error && <Alert>{errorMessage(overLimit.error)}</Alert>}
          <table className="w-full">
            <tbody>
              {i.lines.map((l, k) => (
                <tr key={k} className="border-b border-mist">
                  <td className="py-1.5 whitespace-pre-wrap">{l.description}</td>
                  <td className="py-1.5 text-right">{formatCents(l.amountCents)}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="py-1.5">Total</td>
                <td className="py-1.5 text-right">{formatCents(i.totalCents)}</td>
              </tr>
              <tr>
                <td className="py-1">Paid</td>
                <td className="py-1 text-right">{formatCents(i.paidCents)}</td>
              </tr>
              {i.creditedCents > 0 && (
                <tr>
                  <td className="py-1">Credits and write-offs</td>
                  <td className="py-1 text-right">{formatCents(i.creditedCents)}</td>
                </tr>
              )}
              <tr className="font-semibold">
                <td className="py-1">Balance</td>
                <td className="py-1 text-right">{formatCents(i.balanceCents)}</td>
              </tr>
            </tbody>
          </table>
          {i.memo && <RichText className="text-ink-soft" text={i.memo} />}
          <div>
            <div className="mb-1 font-semibold text-ink-soft">Payments</div>
            {!i.payments.length ? (
              <p className="text-muted">None yet.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {i.payments.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{formatCents(p.amountCents)}</span>
                    <span>{PAYMENT_METHOD_LABELS[p.method]}</span>
                    {p.surchargeCents > 0 && <span className="text-muted">+ {formatCents(p.surchargeCents)} card surcharge</span>}
                    <Badge tone={p.status === "succeeded" ? (p.method === "credit" || p.method === "writeoff" ? "off" : "ok") : p.status === "pending" ? "wait" : "off"}>{p.status === "succeeded" ? (p.method === "credit" ? "Credit" : p.method === "writeoff" ? "Written off" : "Received") : p.status === "pending" ? "Processing" : p.status === "failed" ? "Failed" : p.status === "void" ? "Void" : "Refunded"}</Badge>
                    <span className="text-muted">
                      {formatDate(p.receivedAt)}
                      {p.reference && (p.method === "check" ? ` · #${p.reference}` : ` · ${p.reference}`)}
                      {p.source !== "crm" && ` · from ${MONEY_SOURCE_LABELS[p.source]}`}
                      {p.recordedBy && ` · ${p.recordedBy.name}`}
                      {p.error && ` · ${p.error}`}
                    </span>
                    {edit && p.source === "crm" && !["card", "ach"].includes(p.method) && (
                      <button className="ml-auto text-xs text-muted hover:text-danger" onClick={() => window.confirm("Remove this payment?") && removePay.mutate(p.id)}>
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

function RecordPayment({ invoice, onClose, onSaved }: { invoice: InvoiceItem; onClose: () => void; onSaved: (i: InvoiceItem) => void }) {
  const [method, setMethod] = useState<"check" | "cash" | "financing" | "other">("check");
  const [amount, setAmount] = useState((invoice.balanceCents / 100).toFixed(2));
  const [reference, setReference] = useState("");
  const [date, setDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [note, setNote] = useState("");
  const cents = /^\d+(\.\d{1,2})?$/.test(amount.replace(/[$,\s]/g, "")) ? Math.round(Number(amount.replace(/[$,\s]/g, "")) * 100) : null;
  const save = useMutation({ mutationFn: () => post<InvoiceItem>(`/api/invoices/${invoice.id}/payments`, { method, amountCents: cents, reference, receivedAt: date, note }), onSuccess: onSaved });
  return (
    <Dialog
      title={`Record a payment on invoice ${invoice.number}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!cents} onClick={() => save.mutate()}>
            Record {cents ? formatCents(cents) : ""}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField label="How" value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
            <option value="check">Check</option>
            <option value="cash">Cash</option>
            <option value="financing">Financing</option>
            <option value="other">Other</option>
          </SelectField>
          <Field label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} hint={`Balance ${formatCents(invoice.balanceCents)}`} />
          <Field label={method === "check" ? "Check #" : "Reference"} value={reference} onChange={(e) => setReference(e.target.value)} />
          <Field label="Received" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <Field label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  );
}
